import React, { useEffect, useState } from "react";
import { custApi } from "../../lib/customerApi";
import { toast } from "sonner";
import { MapPin, X, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * Iter 177 — Location update prompt.
 *
 * When a customer's saved service address has invalid coords (missing,
 * (0,0), or outside the India bounding box), we ask them ONCE per
 * login session to share their current GPS. Their answer updates
 * `customer.latitude/longitude` so Ops screens (Live Ops, Assignment
 * matrix, EagleEye) can finally see them.
 *
 * The modal is skippable — but it re-appears on the next login until
 * a valid coord is saved. `dismissed` is stored in sessionStorage so
 * the same tab doesn't re-prompt in a loop after "Skip".
 */
export default function LocationUpdatePrompt() {
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const alreadyDismissed = sessionStorage.getItem("me:loc-prompt-skipped") === "1";
    if (alreadyDismissed) { setDismissed(true); return; }
    custApi.get("/me/location/status")
      .then(r => {
        setNeedsUpdate(!!r.data?.needs_update);
        setReason(r.data?.reason || "");
      })
      .catch(() => {});
  }, []);

  const skip = () => {
    sessionStorage.setItem("me:loc-prompt-skipped", "1");
    setDismissed(true);
  };

  const fetchAndSave = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser can't share GPS. Add your address manually in Profile.");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude, accuracy } = pos.coords;
          await custApi.post("/me/location/update", {
            latitude, longitude, accuracy_m: accuracy,
          });
          toast.success("Location saved · thank you!");
          setNeedsUpdate(false);
          sessionStorage.removeItem("me:loc-prompt-skipped");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Couldn't save your location. Please try again.");
        } finally {
          setBusy(false);
        }
      },
      (err) => {
        setBusy(false);
        toast.error(
          err.code === 1
            ? "Location permission was denied. Please enable it in your browser settings."
            : `GPS unavailable: ${err.message || "unknown error"}`
        );
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  };

  if (!needsUpdate || dismissed) return null;

  const headline = {
    missing: "Please share your service location",
    zero_zero: "Your saved location isn't valid",
    out_of_country: "Your saved location looks off",
  }[reason] || "Please share your location";
  const subline = {
    missing: "We couldn't find a service address on your profile. Share your current spot so our field team can reach you.",
    zero_zero: "The location on your profile is a placeholder (0,0). Tap Share Location so we can update it.",
    out_of_country: "Your saved location falls outside India. Please refresh it so pickups can be planned correctly.",
  }[reason] || "Share your current spot so our field team can reach you.";

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center px-4"
         data-testid="me-loc-prompt">
      <div className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#2D5A27] to-[#3A7033] text-white p-5">
          <button onClick={skip}
                  data-testid="me-loc-skip"
                  className="absolute top-3 right-3 h-8 w-8 inline-flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30">
            <X size={14} weight="bold"/>
          </button>
          <div className="w-12 h-12 rounded-full bg-white/20 inline-flex items-center justify-center mb-2">
            <MapPin size={22} weight="fill"/>
          </div>
          <div className="font-heading text-xl">{headline}</div>
          <div className="text-[13px] text-white/85 mt-1 leading-relaxed">{subline}</div>
        </div>
        {/* Body */}
        <div className="p-5 space-y-3">
          <ul className="text-xs text-[#5C5A55] space-y-1.5">
            <li className="flex items-start gap-2">
              <CheckCircle size={12} weight="fill" color="#2D5A27" className="mt-0.5 flex-shrink-0"/>
              <span>Helps our field team find you faster.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={12} weight="fill" color="#2D5A27" className="mt-0.5 flex-shrink-0"/>
              <span>Only used for service delivery — not shared publicly.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={12} weight="fill" color="#2D5A27" className="mt-0.5 flex-shrink-0"/>
              <span>You can change it anytime in Profile → Address.</span>
            </li>
          </ul>
          <button onClick={fetchAndSave}
                  disabled={busy}
                  data-testid="me-loc-share"
                  className="w-full h-12 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2">
            {busy ? (
              <><ArrowsClockwise size={14} className="animate-spin"/>Locating…</>
            ) : (
              <><MapPin size={14} weight="fill"/>Share my current location</>
            )}
          </button>
          <button onClick={skip}
                  data-testid="me-loc-skip-2"
                  className="w-full h-10 text-sm text-[#5C5A55] hover:text-[#2D5A27]">
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
